<template>
  <div class="modern-auth-wrapper">
    <canvas id="blobCanvas" ref="blobCanvas"></canvas>

    <div class="auth-container">
      <div class="side-visual">
        <div class="fluid-logo-container">
          <img
            :src="staffSyncLogo"
            @error="handleLogoError"
            alt="STAFF SYNC Logo"
            class="fluid-logo"
          >
          <div class="fluid-orb-overlay"></div>
        </div>

        <div class="brand-title">STAFF SYNC</div>
        <div class="brand-sub">Account recovery</div>

        <div class="status-pill">
          <div class="status-dot"></div>
          Secure password reset
        </div>
      </div>

      <div class="form-side">
        <!-- Request step -->
        <template v-if="currentStep === 'request'">
          <button type="button" class="back-link" @click="goToLogin">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <line x1="19" y1="12" x2="5" y2="12"/>
              <polyline points="12 19 5 12 12 5"/>
            </svg>
            Back to sign in
          </button>

          <div class="step-icon step-icon--lock" aria-hidden="true">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <rect x="3" y="11" width="18" height="11" rx="2"/>
              <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
            </svg>
          </div>

          <div class="welcome-text">Forgot password?</div>
          <div class="welcome-sub">
            Enter your email or ID number and we'll send a temporary password to your registered email.
          </div>

          <div class="field">
            <label class="field-label" for="identifier">Email or ID number</label>
            <div class="input-wrap">
              <span class="input-icon">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                  <circle cx="12" cy="7" r="4"/>
                </svg>
              </span>
              <input
                id="identifier"
                v-model="identifier"
                type="text"
                class="fluid-input"
                placeholder="you@company.com or ID number"
                :disabled="isLoading"
                autocomplete="username"
                @keyup.enter="requestReset"
              />
            </div>
          </div>

          <div
            class="alert-box"
            :class="{
              show: alert.success || alert.info || alert.failed,
              ok: alert.success,
              info: alert.info,
              err: alert.failed
            }"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
              <circle cx="12" cy="12" r="10"/>
              <line x1="12" y1="8" x2="12" y2="12"/>
              <line x1="12" y1="16" x2="12.01" y2="16"/>
            </svg>
            <span>{{ message }}</span>
          </div>

          <button class="btn-sign" type="button" :disabled="isLoading || !identifier.trim()" @click="requestReset">
            <template v-if="!isLoading">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                <polyline points="22,6 12,13 2,6"/>
              </svg>
              <span>Send reset email</span>
            </template>
            <template v-else>
              <div class="spinner"></div>
              <span>Sending…</span>
            </template>
          </button>
        </template>

        <!-- Success step -->
        <template v-else>
          <div class="step-icon step-icon--success" aria-hidden="true">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
              <polyline points="22 4 12 14.01 9 11.01"/>
            </svg>
          </div>

          <div class="welcome-text">Check your email</div>
          <div class="welcome-sub">
            We've sent a temporary password to your registered email address.
          </div>

          <div class="steps-card">
            <p class="steps-card__title">What to do next</p>
            <ol class="steps-card__list">
              <li>Open the email from StaffSync</li>
              <li>Sign in with the temporary password</li>
              <li>Change your password immediately</li>
              <li>Check spam if you don't see it within a few minutes</li>
            </ol>
          </div>

          <button class="btn-sign" type="button" @click="goToLogin">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
              <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"/>
              <polyline points="10 17 15 12 10 7"/>
              <line x1="15" y1="12" x2="3" y2="12"/>
            </svg>
            <span>Go to sign in</span>
          </button>

          <button type="button" class="text-action" :disabled="isLoading" @click="resendEmail">
            Didn't receive it? Resend email
          </button>
        </template>

        <div class="foot">© {{ currentYear }} <span>STAFF SYNC</span>. All rights reserved.</div>
      </div>
    </div>
  </div>
</template>

<script>
import { authUrl, execute } from "@/api";
import router from "@/router";

export default {
  name: "ForgotPassword",
  data() {
    return {
      staffSyncLogo: require('@/assets/staffsynclogo.png'),
      logoFallbackIndex: 0,
      identifier: '',
      isLoading: false,
      message: '',
      alert: {
        success: false,
        info: false,
        failed: false
      },
      currentStep: 'request',
      currentYear: new Date().getFullYear()
    };
  },
  mounted() {
    this.initBlobCanvas();
    this.initFluidLogoAnimation();
  },
  methods: {
    handleLogoError() {
      const fallbackPaths = [
        '/staffsynclogo.png',
        '/public/staffsynclogo.png',
        '/assets/staffsynclogo.png'
      ];
      if (this.logoFallbackIndex < fallbackPaths.length) {
        this.staffSyncLogo = fallbackPaths[this.logoFallbackIndex];
        this.logoFallbackIndex += 1;
      }
    },

    clearAlerts() {
      this.alert.success = false;
      this.alert.info = false;
      this.alert.failed = false;
    },

    goToLogin() {
      router.push('/');
    },

    async requestReset() {
      if (!this.identifier || this.identifier.trim() === '') {
        this.clearAlerts();
        this.message = 'Please enter your email or ID number';
        this.alert.failed = true;
        return;
      }

      this.clearAlerts();
      this.message = 'Sending reset email…';
      this.alert.info = true;
      this.isLoading = true;

      const data = new FormData();
      data.append('function', 'forgotPassword');
      data.append('identifier', this.identifier.trim());

      try {
        const res = await execute(data, authUrl);
        this.clearAlerts();
        this.isLoading = false;

        if (res.data.success) {
          this.currentStep = 'success';
          this.message = res.data.message || 'Password reset email sent successfully!';
        } else {
          this.alert.failed = true;
          this.message = res.data.message || 'Failed to send reset email. Please try again.';
        }
      } catch (error) {
        this.clearAlerts();
        this.isLoading = false;
        this.alert.failed = true;
        this.message = error.message || 'An error occurred. Please try again.';
        console.error('Forgot password error:', error);
      }
    },

    async resendEmail() {
      this.currentStep = 'request';
      this.clearAlerts();
      await this.requestReset();
    },

    initBlobCanvas() {
      const canvas = this.$refs.blobCanvas;
      if (!canvas) return;

      const ctx = canvas.getContext('2d');
      let W;
      let H;
      let t = 0;
      const mouse = { x: 0.5, y: 0.5 };

      const blobs = [
        { ox: 0.2, oy: 0.3, r: 280, c: 'rgba(74, 58, 255, 0.18)', phase: 0, amp: 0.06 },
        { ox: 0.75, oy: 0.65, r: 320, c: 'rgba(21, 44, 86, 0.14)', phase: 1.2, amp: 0.05 },
        { ox: 0.5, oy: 0.85, r: 240, c: 'rgba(120, 80, 255, 0.12)', phase: 2.4, amp: 0.07 }
      ];

      const resize = () => {
        W = canvas.width = window.innerWidth;
        H = canvas.height = window.innerHeight;
      };

      const draw = () => {
        ctx.clearRect(0, 0, W, H);
        t += 0.014;
        blobs.forEach((b) => {
          const bx = (b.ox + Math.sin(t + b.phase) * b.amp + (mouse.x - 0.5) * 0.05) * W;
          const by = (b.oy + Math.cos(t * 0.75 + b.phase) * b.amp * 0.8 + (mouse.y - 0.5) * 0.05) * H;
          const grd = ctx.createRadialGradient(bx, by, 0, bx, by, b.r);
          grd.addColorStop(0, b.c);
          grd.addColorStop(1, 'rgba(0,0,0,0)');
          ctx.beginPath();
          ctx.arc(bx, by, b.r, 0, Math.PI * 2);
          ctx.fillStyle = grd;
          ctx.fill();
        });
        requestAnimationFrame(draw);
      };

      resize();
      draw();
      window.addEventListener('resize', resize);
      document.addEventListener('mousemove', (e) => {
        mouse.x = e.clientX / window.innerWidth;
        mouse.y = e.clientY / window.innerHeight;
      });
    },

    initFluidLogoAnimation() {
      const logo = document.querySelector('.fluid-logo');
      const overlay = document.querySelector('.fluid-orb-overlay');
      if (!logo) return;

      let angle = 0;
      let scale = 1;
      let direction = 1;

      setInterval(() => {
        angle += 0.02;
        const yOffset = Math.sin(angle) * 8;
        const xOffset = Math.cos(angle * 0.7) * 5;

        logo.style.transform = `translate(${xOffset}px, ${yOffset}px) scale(${scale})`;

        scale += direction * 0.003;
        if (scale >= 1.03) direction = -1;
        if (scale <= 0.97) direction = 1;

        if (overlay) {
          overlay.style.transform = `translate(-50%, -50%) rotate(${angle * 30}deg) scale(1.1)`;
        }
      }, 50);
    }
  }
};
</script>

<style scoped>
*, *::before, *::after {
  box-sizing: border-box;
  margin: 0;
  padding: 0;
}

.modern-auth-wrapper {
  position: fixed;
  inset: 0;
  width: 100vw;
  height: 100vh;
  font-family: 'DM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  background: #eeeef6;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

#blobCanvas {
  position: fixed;
  inset: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 0;
}

.auth-container {
  position: relative;
  z-index: 1;
  display: grid;
  grid-template-columns: 1fr 1fr;
  width: 90%;
  max-width: 860px;
  min-height: 520px;
  max-height: 90vh;
  background: rgba(255, 255, 255, 0.82);
  backdrop-filter: blur(24px);
  -webkit-backdrop-filter: blur(24px);
  border: 1px solid rgba(255, 255, 255, 0.95);
  border-radius: 32px;
  box-shadow: 0 8px 48px rgba(60, 60, 120, 0.14), 0 1px 0 rgba(255, 255, 255, 0.9) inset;
  overflow: hidden;
  animation: cardIn 0.55s cubic-bezier(0.4, 0, 0.2, 1) both;
}

@keyframes cardIn {
  from { opacity: 0; transform: translateY(24px); }
  to { opacity: 1; transform: translateY(0); }
}

.side-visual {
  position: relative;
  background: #152c56;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 32px;
  overflow: hidden;
}

.side-visual::before {
  content: '';
  position: absolute;
  inset: 0;
  background:
    radial-gradient(circle at 30% 20%, rgba(255, 255, 255, 0.18) 0%, transparent 55%),
    radial-gradient(circle at 80% 80%, rgba(120, 80, 255, 0.3) 0%, transparent 50%);
}

.fluid-logo-container {
  position: relative;
  z-index: 1;
  width: 180px;
  height: 180px;
  margin-bottom: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.fluid-logo {
  position: relative;
  z-index: 2;
  width: 140px;
  height: auto;
  max-height: 140px;
  object-fit: contain;
  filter: drop-shadow(0 4px 20px rgba(0, 0, 0, 0.2));
  transition: transform 0.1s ease-out;
}

.fluid-orb-overlay {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 170px;
  height: 170px;
  transform: translate(-50%, -50%);
  background: radial-gradient(circle, rgba(255, 255, 255, 0.3) 0%, rgba(255, 255, 255, 0) 70%);
  border-radius: 50%;
  pointer-events: none;
  transition: transform 0.1s linear;
}

.brand-title {
  font-family: 'Syne', 'DM Sans', sans-serif;
  font-size: 23px;
  font-weight: 700;
  color: #fff;
  letter-spacing: -0.5px;
  text-align: center;
  position: relative;
  z-index: 1;
  margin-bottom: 6px;
}

.brand-sub {
  font-size: 11px;
  color: rgba(255, 255, 255, 0.65);
  text-align: center;
  position: relative;
  z-index: 1;
  letter-spacing: 0.7px;
  text-transform: uppercase;
}

.status-pill {
  position: absolute;
  bottom: 28px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(255, 255, 255, 0.14);
  border: 1px solid rgba(255, 255, 255, 0.26);
  border-radius: 20px;
  padding: 6px 14px;
  font-size: 11px;
  color: rgba(255, 255, 255, 0.82);
  display: flex;
  align-items: center;
  gap: 7px;
  backdrop-filter: blur(8px);
  z-index: 1;
  white-space: nowrap;
}

.status-dot {
  width: 7px;
  height: 7px;
  background: #4ade80;
  border-radius: 50%;
  animation: pulse 2s ease-in-out infinite;
}

@keyframes pulse {
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.55; transform: scale(0.75); }
}

.form-side {
  padding: 44px 40px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  overflow-y: auto;
}

.back-link {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 20px;
  padding: 0;
  border: none;
  background: none;
  font-size: 12.5px;
  font-weight: 500;
  color: #253759;
  cursor: pointer;
  transition: opacity 0.2s;
}

.back-link:hover {
  opacity: 0.75;
}

.step-icon {
  width: 52px;
  height: 52px;
  border-radius: 16px;
  display: grid;
  place-items: center;
  margin-bottom: 18px;
}

.step-icon--lock {
  background: rgba(21, 44, 86, 0.08);
  color: #152c56;
}

.step-icon--success {
  background: rgba(74, 222, 128, 0.12);
  color: #15803d;
}

.welcome-text {
  font-family: 'Syne', 'DM Sans', sans-serif;
  font-size: 27px;
  font-weight: 700;
  color: #1a1a2e;
  letter-spacing: -0.5px;
  margin-bottom: 4px;
}

.welcome-sub {
  font-size: 13px;
  color: #8892a4;
  margin-bottom: 26px;
  line-height: 1.55;
  max-width: 34ch;
}

.field {
  margin-bottom: 18px;
}

.field-label {
  font-size: 10.5px;
  font-weight: 600;
  color: #253759;
  letter-spacing: 0.7px;
  text-transform: uppercase;
  margin-bottom: 7px;
  display: block;
}

.input-wrap {
  position: relative;
}

.input-icon {
  position: absolute;
  left: 14px;
  top: 50%;
  transform: translateY(-50%);
  color: #aab0bf;
  pointer-events: none;
  display: flex;
  align-items: center;
}

.fluid-input {
  width: 100%;
  padding: 12px 14px 12px 42px;
  font-family: 'DM Sans', sans-serif;
  font-size: 14px;
  color: #1a1a2e;
  background: rgba(248, 249, 255, 0.85);
  border: 1.5px solid #e2e5f0;
  border-radius: 12px;
  outline: none;
  transition: border-color 0.22s, box-shadow 0.22s, background 0.22s;
}

.fluid-input:focus {
  border-color: #253759;
  background: #fff;
  box-shadow: 0 0 0 3.5px rgba(74, 58, 255, 0.11);
}

.fluid-input::placeholder {
  color: #b8bfcc;
}

.fluid-input:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.alert-box {
  padding: 10px 14px;
  border-radius: 10px;
  font-size: 12.5px;
  display: none;
  align-items: center;
  gap: 9px;
  margin-bottom: 16px;
}

.alert-box.show {
  display: flex;
}

.alert-box.err {
  background: rgba(239, 68, 68, 0.08);
  color: #b91c1c;
  border: 1px solid rgba(239, 68, 68, 0.22);
}

.alert-box.info {
  background: rgba(74, 58, 255, 0.07);
  color: #3730a3;
  border: 1px solid rgba(74, 58, 255, 0.2);
}

.alert-box.ok {
  background: rgba(74, 222, 128, 0.09);
  color: #166534;
  border: 1px solid rgba(74, 222, 128, 0.25);
}

.steps-card {
  margin-bottom: 22px;
  padding: 16px 18px;
  border-radius: 14px;
  background: rgba(248, 249, 255, 0.9);
  border: 1px solid #e8ebf5;
}

.steps-card__title {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #253759;
  margin-bottom: 10px;
}

.steps-card__list {
  margin: 0;
  padding-left: 1.15rem;
  font-size: 13px;
  color: #5b6478;
  line-height: 1.65;
}

.steps-card__list li + li {
  margin-top: 4px;
}

.btn-sign {
  width: 100%;
  padding: 14px;
  font-family: 'DM Sans', sans-serif;
  font-size: 15px;
  font-weight: 600;
  color: #fff;
  background: #152c56;
  border: none;
  border-radius: 14px;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 9px;
  letter-spacing: 0.2px;
  transition: transform 0.2s, box-shadow 0.2s;
}

.btn-sign::after {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.15) 0%, transparent 60%);
  border-radius: inherit;
  pointer-events: none;
}

.btn-sign:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 8px 30px rgba(74, 58, 255, 0.35);
}

.btn-sign:active:not(:disabled) {
  transform: translateY(0);
  box-shadow: 0 2px 12px rgba(74, 58, 255, 0.28);
}

.btn-sign:disabled {
  opacity: 0.68;
  cursor: not-allowed;
}

.text-action {
  margin-top: 14px;
  width: 100%;
  padding: 0;
  border: none;
  background: none;
  font-size: 12.5px;
  font-weight: 500;
  color: #253759;
  cursor: pointer;
  text-align: center;
  transition: opacity 0.2s;
}

.text-action:hover:not(:disabled) {
  opacity: 0.75;
  text-decoration: underline;
}

.text-action:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.spinner {
  width: 17px;
  height: 17px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: #fff;
  border-radius: 50%;
  animation: spin 0.75s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.foot {
  text-align: center;
  padding-top: 22px;
  font-size: 11px;
  color: #b0b7c3;
  letter-spacing: 0.3px;
}

.foot span {
  color: #4a3aff;
  font-weight: 600;
}

@media (max-width: 680px) {
  .auth-container {
    grid-template-columns: 1fr;
    width: 95%;
    max-width: 480px;
  }

  .side-visual {
    display: none;
  }

  .form-side {
    padding: 36px 26px;
  }
}
</style>
